import type { CSSProperties, ElementType, ReactNode, RefObject } from "react";

import { useScrollReveal } from "@/hooks/useScrollReveal";

type AnimVariant = "fadeUp" | "fadeIn" | "fadeLeft" | "fadeRight";

interface ScrollRevealProps {
  children: ReactNode;
  delay?: number;
  duration?: number;
  variant?: AnimVariant;
  className?: string;
  as?: ElementType;
  threshold?: number;
}

const variantStyles: Record<AnimVariant, { hidden: CSSProperties; visible: CSSProperties }> = {
  fadeUp: {
    hidden: { opacity: 0, transform: "translateY(22px)" },
    visible: { opacity: 1, transform: "translateY(0)" },
  },
  fadeIn: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
  fadeLeft: {
    hidden: { opacity: 0, transform: "translateX(-20px)" },
    visible: { opacity: 1, transform: "translateX(0)" },
  },
  fadeRight: {
    hidden: { opacity: 0, transform: "translateX(20px)" },
    visible: { opacity: 1, transform: "translateX(0)" },
  },
};

export default function ScrollReveal({
  children,
  delay = 0,
  duration = 620,
  variant = "fadeUp",
  className = "",
  as: Tag = "div",
  threshold = 0.12,
}: ScrollRevealProps) {
  const { ref, isVisible } = useScrollReveal({ threshold });
  const styles = variantStyles[variant];

  const style: CSSProperties = {
    transition: `opacity ${duration}ms cubic-bezier(0.22, 1, 0.36, 1), transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1)`,
    transitionDelay: `${delay}ms`,
    ...(isVisible ? styles.visible : styles.hidden),
  };

  return (
    <Tag
      ref={ref as RefObject<HTMLElement>}
      className={className}
      style={style}
    >
      {children}
    </Tag>
  );
}
